import {
  CloseButton,
  Dialog,
  type DialogRootProps,
  Portal,
} from '@chakra-ui/react'
import React, { createContext, useContext } from 'react'

import { preventEscapeFromNestedPopup } from '../utils/keyboardOwnership'

interface ModalLabels {
  closeLabel?: string
}

const ModalLabelsContext = createContext<ModalLabels>({})

// Zag focuses the close trigger first in alertdialogs; returning null instead
// falls through to the first tabbable without `data-no-autofocus`.
const noInitialFocusOverride = () => null

/** Supplies translated default labels to every Modal and ConfirmDialog below it. */
export const ModalLabelsProvider: React.FC<
  ModalLabels & { children: React.ReactNode }
> = ({ children, ...labels }) => (
  <ModalLabelsContext.Provider value={labels}>
    {children}
  </ModalLabelsContext.Provider>
)

export interface ModalProps {
  /** Whether the modal is open */
  open: boolean
  /** Callback when the modal should close (e.g. backdrop click, close trigger, escape) */
  onOpenChange: (details: { open: boolean }) => void
  /** Title shown in the header. Omit for dialogs that have no title. */
  title?: string
  /** Main body content */
  children: React.ReactNode
  /** Optional footer content (e.g. action buttons) */
  footer?: React.ReactNode
  /** Max width of the content (default 400px) */
  maxWidth?: string | number
  /** Max height of the content; enables overflow scrolling when set */
  maxHeight?: string | number
  /** Header title size. Defaults to lg. */
  titleFontSize?: string
  /** Dialog placement. Defaults to center. Raw Chakra dialogs default to top. */
  placement?: DialogRootProps['placement']
  /** ARIA role override (e.g. "alertdialog" for destructive confirmations) */
  role?: 'dialog' | 'alertdialog'
  /**
   * Whether a pointer-down or focus outside the modal closes it.
   * Defaults to Chakra's behaviour (true, false for alertdialog).
   */
  closeOnInteractOutside?: boolean
  /** Whether Escape closes the modal (Chakra default: true) */
  closeOnEscape?: boolean
  /** Accessible name for the close button. Defaults to ModalLabelsProvider's value, then "Close". */
  closeLabel?: string
  /** Disables the close button, Escape, and backdrop close (e.g. while a request is pending) */
  closeDisabled?: boolean
  /** Stacking order for the backdrop; the dialog sits one layer above it */
  zIndex?: number
  /**
   * Element focused on open. Defaults to the first focusable element in the
   * body or footer, never the header close button.
   */
  initialFocusEl?: () => HTMLElement | null
}

/**
 * Base modal component wrapping Chakra Dialog.
 * Provides consistent layout: header with title + close, body, optional footer.
 */
export const Modal: React.FC<ModalProps> = ({
  open,
  onOpenChange,
  title,
  children,
  footer,
  maxWidth = '400px',
  maxHeight,
  titleFontSize = 'lg',
  placement = 'center',
  role,
  closeOnInteractOutside,
  closeOnEscape,
  closeLabel,
  closeDisabled = false,
  zIndex,
  initialFocusEl,
}) => {
  const labels = useContext(ModalLabelsContext)
  const resolvedCloseLabel = closeLabel ?? labels.closeLabel ?? 'Close'

  const handleOpenChange = (details: { open: boolean }) => {
    if (!details.open && !closeDisabled) {
      onOpenChange(details)
    }
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={handleOpenChange}
      placement={placement}
      role={role}
      closeOnInteractOutside={closeDisabled ? false : closeOnInteractOutside}
      closeOnEscape={closeDisabled ? false : closeOnEscape}
      onEscapeKeyDown={preventEscapeFromNestedPopup}
      initialFocusEl={initialFocusEl ?? noInitialFocusOverride}
    >
      <Portal>
        <Dialog.Backdrop backdropFilter="blur(4px)" zIndex={zIndex} />
        <Dialog.Positioner zIndex={zIndex != null ? zIndex + 1 : undefined}>
          <Dialog.Content
            maxW={maxWidth}
            maxH={maxHeight}
            overflow={maxHeight != null ? 'auto' : undefined}
            bg="bg.panel"
            borderRadius="lg"
            boxShadow="xl"
            borderWidth="1px"
            borderColor="border.default"
            p={0}
          >
            <Dialog.Header
              p={5}
              borderBottomWidth={1}
              borderColor="border.default"
            >
              {title ? (
                <Dialog.Title fontSize={titleFontSize} fontWeight="semibold">
                  {title}
                </Dialog.Title>
              ) : null}
              <Dialog.CloseTrigger asChild>
                <CloseButton
                  size="sm"
                  aria-label={resolvedCloseLabel}
                  disabled={closeDisabled}
                  data-no-autofocus
                />
              </Dialog.CloseTrigger>
            </Dialog.Header>
            <Dialog.Body p={5}>{children}</Dialog.Body>
            {footer != null ? (
              <Dialog.Footer
                p={5}
                borderTopWidth={1}
                borderColor="border.default"
              >
                {footer}
              </Dialog.Footer>
            ) : null}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

Modal.displayName = 'Modal'
